import { useEffect, useRef, useState } from "react";

import { Dialog } from "components/ui/Dialog";
import { HseDetailsNotice } from "components/ui/HseDetailsNotice/HseDetailsNotice";
import { canPatientReachHse } from "features/healthlink/utils/hsePatientReadiness";
import {
  buildIbRedirectUrl,
  withIbReturnUrl,
} from "features/healthlink/utils/ibRedirect";
import { PENDING_IB_REFERRAL_KEY } from "features/referrals/constants";

import style from "./AddReferralForm.module.css";
import { AdditionalInformationsForm } from "./components/AdditionalInformationsForm/AdditionalInformationsForm";
import { GeneralInformationForm } from "./components/GeneralInformationForm/GeneralInformationForm";
import { LungsAdditionalInfoForm } from "./components/LungsAdditionalInfoForm/LungsAdditionalInfoForm";
import { PatientDetailsForm } from "./components/PatientDetailsForm/PatientDetailsForm";
import { ProstateAdditionalInfoForm } from "./components/ProstateAdditionalInfoForm/ProstateAdditionalInfoForm";
import { ReferralForm } from "./components/ReferralForm/ReferralForm";
import { ReviewForm } from "./components/ReviewForm/ReviewForm";
import { Steps } from "./components/Steps/Steps";
import { useReferralData } from "./hooks/useReferralData";
import { useReferralForms } from "./hooks/useReferralForms";
import { useReferralSubmission } from "./hooks/useReferralSubmission";
import { assembleAllFormsData } from "./utils/buildReferralPayloads";

// Feature flag: when "true", a specialist/IB referral submit lands on the
// "Specialist Referral" step (the old flow — user chooses auto-redirect vs.
// copy-link there). Default (unset) redirects straight to Healthlink on submit.
const SHOW_SPECIALIST_REFERRAL_STEP =
  process.env.REACT_APP_IB_REFERRAL_STEP === "true";

export const AddReferralForm = ({
  patientId,
  patient,
  wrapperStyle = {},
  onSuccess,
  staffMember,
  patientBaseline,
  patientPreferences,
  patientSocial,
  medicalHistory,
  familyMedicalHistory,
  allergies,
  consultationsList,
  prefillErrors = [],
}) => {
  const today = new Date();
  const [activeStep, setActiveStep] = useState("generalInformation");
  // IB redirect fallback: if the automatic redirect to the HealthLink Integrated
  // Browser doesn't take the user away, we surface a modal with the copyable
  // link + instructions so the referral can still be completed manually.
  const [ibFallback, setIbFallback] = useState(null);
  const [isIbFallbackOpen, setIsIbFallbackOpen] = useState(false);
  const ibFallbackTimer = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => () => clearTimeout(ibFallbackTimer.current), []);
  const hasRenderedInitialStep = useRef(false);

  useEffect(() => {
    if (!hasRenderedInitialStep.current) {
      hasRenderedInitialStep.current = true;
      return;
    }

    wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeStep]);

  const {
    generalInformationForm,
    patientDetailsForm,
    referralForm,
    prostateAdditionalInfoForm,
    lungsAdditionalInfoForm,
    isLungsReferral,
    isProstateReferral,
    practiceOutOfHoursPhone,
  } = useReferralForms({
    patient,
    patientPreferences,
    patientSocial,
    patientBaseline,
    medicalHistory,
    allergies,
    familyMedicalHistory,
    staffMember,
    consultationsList,
    today,
  });

  const {
    healthlinkAvailableServices,
    laboratoryResults,
    radiologyResults,
    documents,
    latestPsaResult,
    isLoadingHospitals,
    hospitalsError,
    selectedHospital,
    selectedFacility,
    isSpecialist,
    gpDetails,
  } = useReferralData({
    patientId,
    generalInformationForm,
    isProstateReferral,
  });

  const allFormsData = assembleAllFormsData({
    generalInformationForm,
    patientDetailsForm,
    referralForm,
    prostateAdditionalInfoForm,
    lungsAdditionalInfoForm,
    isLungsReferral,
    isProstateReferral,
    patientId,
    selectedFacility,
  });

  // Automatic Integrated Browser (IB) redirect: when the BE returns a link for a
  // specialist/IB referral, send the browser out to the external HealthLink site,
  // appending a redirectUrl back to this page carrying the referralId. On return,
  // the referralId drives the auto status-check effect in PatientReferralsPage.
  //
  // HealthLink drops our redirectUrl query params (it returns only its own
  // ?controlId), so the referralId in the URL cannot be relied on. We also stash
  // it in sessionStorage — it survives the same-tab redirect and is read back on
  // return regardless of what HealthLink appends.
  const navigateToIbReferral = (link, referralId) => {
    const returnUrl = new URL(window.location.href);
    if (referralId) {
      returnUrl.searchParams.set("referralId", String(referralId));
      sessionStorage.setItem(PENDING_IB_REFERRAL_KEY, String(referralId));
    }
    const linkWithRedirectUrl = withIbReturnUrl(link, returnUrl.toString());

    // Keep the link so the fallback modal can show it if the redirect fails.
    setIbFallback({ link, referralId });
    window.location.href = buildIbRedirectUrl(linkWithRedirectUrl);

    // A successful redirect unloads this page before the timer fires. If we're
    // still here after a moment, the redirect didn't work — open the fallback
    // modal with the copyable link + step-by-step instructions.
    clearTimeout(ibFallbackTimer.current);
    ibFallbackTimer.current = setTimeout(
      () => setIsIbFallbackOpen(true),
      2500
    );
  };

  const { handleSendReferral, isPending } = useReferralSubmission({
    allFormsData,
    selectedFacility,
    staffMember,
    isSpecialist,
    isLungsReferral,
    isProstateReferral,
    gpDetails,
    onSuccess,
    // On submit of a specialist/IB referral: with the flag on, advance to the
    // "Specialist Referral" step (old flow); otherwise redirect straight to
    // Healthlink (the fallback modal still surfaces the same card if the
    // redirect doesn't take — see navigateToIbReferral).
    onSpecialistReferral: ({ link, referralId }) => {
      if (SHOW_SPECIALIST_REFERRAL_STEP) {
        setIbFallback({ link, referralId });
        setActiveStep("specialistReferral");
        return;
      }

      navigateToIbReferral(link, referralId);
    },
  });

  // Nothing behind this wizard does anything but send to HealthLink, so when the patient
  // cannot be sent there is no form worth filling: the notice replaces it rather than
  // sitting above four steps that end in a refusal (KIN-1733).
  if (!canPatientReachHse(patient)) {
    return (
      <section ref={wrapperRef} className={style.wrapper} style={wrapperStyle}>
        <HseDetailsNotice
          patient={patient}
          patientId={patientId}
          submissionName="as a referral"
        />
      </section>
    );
  }

  return (
    <section ref={wrapperRef} className={style.wrapper} style={wrapperStyle}>
      <Steps
        activeStep={activeStep}
        isLungsReferral={isLungsReferral}
        isProstateReferral={isProstateReferral}
        isSpecialistReferral={isSpecialist}
      />
      {activeStep === "generalInformation" && (
        <GeneralInformationForm
          availableReferralTypes={selectedHospital?.referralTypes ?? []}
          hospitals={healthlinkAvailableServices ?? []}
          form={generalInformationForm}
          isLoadingHospitals={isLoadingHospitals}
          hospitalsError={hospitalsError}
          onContinue={() => {
            setActiveStep("patientDetails");
          }}
        />
      )}
      {activeStep === "patientDetails" && (
        <PatientDetailsForm
          isProstateReferral={isProstateReferral}
          isLungReferral={isLungsReferral}
          form={patientDetailsForm}
          practiceOutOfHoursPhone={practiceOutOfHoursPhone}
          onBack={() => {
            setActiveStep("generalInformation");
          }}
          onContinue={() => {
            setActiveStep("referralForm");
          }}
        />
      )}
      {activeStep === "referralForm" && (
        <ReferralForm
          prefillErrors={prefillErrors}
          selectedFacility={selectedFacility}
          laboratoryResults={laboratoryResults}
          radiologyResults={radiologyResults}
          isProstateReferral={isProstateReferral}
          isLungReferral={isLungsReferral}
          documents={documents}
          onBack={() => {
            setActiveStep("patientDetails");
          }}
          onContinue={() => {
            if (isLungsReferral) {
              setActiveStep("lungsDetails");
            } else if (isProstateReferral) {
              setActiveStep("prostateDetails");
            } else {
              setActiveStep("review");
            }
          }}
          form={referralForm}
          data={allFormsData}
        />
      )}
      {activeStep === "lungsDetails" && (
        <LungsAdditionalInfoForm
          onBack={() => setActiveStep("referralForm")}
          onContinue={() => {
            setActiveStep("review");
          }}
          form={lungsAdditionalInfoForm}
          data={allFormsData}
        />
      )}
      {activeStep === "prostateDetails" && (
        <ProstateAdditionalInfoForm
          onBack={() => setActiveStep("referralForm")}
          onContinue={() => {
            setActiveStep("review");
          }}
          form={prostateAdditionalInfoForm}
          data={allFormsData}
          latestPsaResult={latestPsaResult}
        />
      )}
      {activeStep === "review" && (
        <ReviewForm
          onBack={() => {
            if (isLungsReferral) {
              setActiveStep("lungsDetails");
            } else if (isProstateReferral) {
              setActiveStep("prostateDetails");
            } else {
              setActiveStep("referralForm");
            }
          }}
          data={allFormsData}
          isPending={isPending}
          isProstateReferral={isProstateReferral}
          isLungReferral={isLungsReferral}
          onSendReferral={handleSendReferral}
        />
      )}
      {activeStep === "specialistReferral" && (
        <div className={style.specialistReferralWrapper}>
          <AdditionalInformationsForm
            link={ibFallback?.link}
            referralId={ibFallback?.referralId}
            onAutoRedirect={() =>
              navigateToIbReferral(ibFallback?.link, ibFallback?.referralId)
            }
            onContinue={() => onSuccess?.()}
            showDraftNotice
          />
        </div>
      )}

      <Dialog
        open={isIbFallbackOpen}
        title="Complete referral in HealthLink"
        onClose={() => setIsIbFallbackOpen(false)}
        width={640}
      >
        <AdditionalInformationsForm
          link={ibFallback?.link}
          referralId={ibFallback?.referralId}
          hideFinishButton={false}
          showDraftNotice
          onContinue={() => {
            setIsIbFallbackOpen(false);
            onSuccess?.();
          }}
        />
      </Dialog>
    </section>
  );
};
